import React, { type ReactNode, forwardRef, useEffect } from "react";
import { createPortal } from "react-dom";

import { CloseIconSlimButton } from "@/design-system/icons/close-icon-slim";
import { isClient } from "@/lib/common";
import { useModalStackContext } from "@/src/contexts/ModelStackContext";
import * as stylex from "@stylexjs/stylex";

import {
    ComposableSpacing,
    type PropsWithStyleX,
    type QueryXsMax,
    colors,
} from "../../../styles/tokens.stylex";

export type IModalV2Props = PropsWithStyleX & {
    id?: string;
    children: React.ReactNode;
    header?: ReactNode;
    footer?: ReactNode;
    headerStyleX?: stylex.StyleXStyles;
    mainStyleX?: stylex.StyleXStyles;
    sectionStyleX?: stylex.StyleXStyles;
    closeButtonStyleX?: stylex.StyleXStyles;
    onClose: () => void;
};

const ModalV2 = forwardRef<HTMLDivElement, IModalV2Props>((props, ref) => {
    const { onCloseModal, onOpenModal } = useModalStackContext();

    useEffect(() => {
        onOpenModal();
        document.body.style.overflow = "hidden";

        return () => {
            onCloseModal();
            document.body.style.overflow = "";
        };
    }, []);

    return (
        isClient &&
        createPortal(
            <div {...stylex.props(styles.modalOverlay, props.styleX)}>
                <section {...stylex.props(styles.modalContent, props.sectionStyleX)} ref={ref}>
                    <header {...stylex.props(styles.defaultPadding, styles.header, props.headerStyleX)}>
                        {props.header}
                    </header>
                    <main {...stylex.props(styles.defaultPadding, styles.main, props.mainStyleX)}>
                        {props.children}
                    </main>
                    {props.footer && (
                        <footer {...stylex.props(styles.defaultPadding, styles.footer)}>
                            {props.footer}
                        </footer>
                    )}
                    <CloseIconSlimButton
                        styleX={[styles.closeButton, props.closeButtonStyleX]}
                        onClick={props.onClose}
                    />
                </section>
            </div>,
            document.body,
        )
    );
});

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    modalOverlay: {
        backgroundPosition: "0% 0%",
        alignItems: "center",
        backgroundColor: "#000000CC",
        backgroundOrigin: "padding-box",
        backgroundRepeat: "no-repeat",
        display: "flex",
        justifyContent: "center",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
    },
    modalContent: {
        borderRadius: 0,
        backgroundColor: "#fff",
        boxShadow: "0 0.2em 0.6em rgba(0, 0, 0, 0.1)",
        display: "grid",
        fontSize: "1rem",
        gridTemplateRows: "auto 1fr auto",
        position: "relative",
        height: { default: null, [xsMax]: "100%" },
        maxHeight: { default: "80vh", [xsMax]: "100%" },
        maxWidth: { default: "80vw", [xsMax]: "100%" },
        width: { default: "500px", [xsMax]: "100%" },
    },
    header: {
        alignItems: "start",
        display: "flex",
        justifyContent: "space-between",
    },
    main: {
        overflow: "auto",
        scrollbarWidth: "none",
        willChange: "scroll-position",
        paddingBottom: 0,
        paddingTop: 0,
        "::-webkit-scrollbar": { display: "none" },
    },
    footer: {
        borderTopColor: colors.fg_Tertiary,
        borderTopStyle: "solid",
        borderTopWidth: "1px",
    },
    defaultPadding: {
        padding: { default: ComposableSpacing.s6, [xsMax]: ComposableSpacing.s4 },
    },
    closeButton: {
        padding: 0,
        borderStyle: "none",
        color: {
            default: colors.fg_Tertiary,
            ":hover": colors.fg_Primary,
        },
        position: "absolute",
        height: "1rem",
        right: 20,
        top: ComposableSpacing.s3,
        width: "1rem",
    },
});

export default ModalV2;
